import addressApi from 'api/requests/address'
import Fieldset from 'components/_UI/Fieldset'
import Loader from 'components/_UI/Loader'
import fieldValidations from 'helpers/fieldValidations'
import masks from 'helpers/masks'
import regex from 'helpers/regex'
import unformat from 'helpers/unformat';
import React, { useEffect, useState } from 'react'
import { AlertCircle } from 'react-feather'
import styled from 'styled-components'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'

export default function BankAddressInfo({
    register,
    setValue,
    getValues,
    setError,
    clearErrors,
    errors,
    isEditing,
    setIsEditing,
    watchCep,
}) {
    const [showFields, setShowFields] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (isEditing) {
            setIsEditing(false)
            setShowFields(true)
            return
        }

        const cep = getValues('cep')
        const unformatCep = unformat.getNumbers(cep)

        if (unformatCep?.length == 8) {
            showAddressFields(cep)
        }
    }, [watchCep])

    function showAddressFields(cep) {
        if (cep) {
            if (!validateCEP(cep)) {
                setShowFields(false)

                return
            }
        }
        getAddress(cep)
        setShowFields(true)
    }

    function validateCEP(cep) {
        return regex.cep.test(cep);
    }

    async function getAddress(cep) {
        setLoading(true)
        try {
            const { data } = await addressApi.getByPostalCode(cep)

            setValue('addressDistrict', data.street.district)
            setValue('addressStreet', data.street.name)
            setValue('addressNumber', '')
            clearErrors('addressDistrict')
            clearErrors('addressStreet')
            setValue('addressNumber')
            setShowFields(true)
        } catch (error) {
            if (error) {
                setShowFields(false)
                setError('cep', { type: 'custom', message: 'CEP inválido!' })
                return
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container>
            <FieldGrid columns={1}>
                <FieldWrapper half>
                    <Fieldset
                        marginTop='6px'
                        placeholder='CEP'
                        label='CEP *'
                        name="cep"
                        register={register}
                        mask={masks.cep}
                        validations={fieldValidations.cepPatterns()}
                        layout='squired'
                    />
                    {errors?.cep?.message && (
                        <FieldError>
                            <AlertCircle size={13} />
                            {errors.cep?.message}
                        </FieldError>
                    )}
                </FieldWrapper>
            </FieldGrid>

            {loading ? (
                <Loader />
            ) : (
                <AddressFields visible={showFields}>
                    <FieldGrid columns={3}>
                        <FieldWrapper>
                            <Fieldset
                                name='addressDistrict'
                                layout='squired'
                                label='Bairro *'
                                register={register}
                                validations={fieldValidations.generic('Bairro')}
                            />
                            {errors?.addressDistrict && (
                                <FieldError>
                                    <AlertCircle size={13} />
                                    {errors.addressDistrict.message}
                                </FieldError>
                            )}
                        </FieldWrapper>

                        <FieldWrapper>
                            <Fieldset
                                name='addressStreet'
                                layout='squired'
                                label='Rua *'
                                register={register}
                                validations={fieldValidations.generic('Rua')}
                            />
                            {errors?.addressStreet && (
                                <FieldError>
                                    <AlertCircle size={13} />
                                    {errors.addressStreet.message}
                                </FieldError>
                            )}
                        </FieldWrapper>

                        <FieldWrapper>
                            <Fieldset
                                name='addressNumber'
                                placeholder='Número'
                                label='Número *'
                                layout='squired'
                                register={register}
                                validations={fieldValidations.addressNumber('Número')}
                            />
                            {errors?.addressNumber && (
                                <FieldError>
                                    <AlertCircle size={13} />
                                    {errors.addressNumber.message}
                                </FieldError>
                            )}
                        </FieldWrapper>
                    </FieldGrid>
                </AddressFields>
            )}
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
`;

const AddressFields = styled.div`
    width: 100%;
    height: 0;
    visibility: hidden;
    opacity: 0;
    transition: all .2s ease-in;

    ${({ visible }) => visible && `
        height: auto;
        visibility: visible;
        opacity: 1;
        margin-top: 8px;
    `};
`;

const FieldGrid = styled.div`
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    margin-bottom: 8px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(${({ columns }) => columns || 1}, 1fr);
    }
`;

const FieldWrapper = styled.div`
    width: 100%;

    ${({ half }) => half && `
        @media screen and (min-width: ${breakpoints.md}) {
            max-width: 50%;
        }
    `}
`;

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 6px;
    padding: 4px 8px;
    background: ${colors.danger}0A;
    border-radius: 6px;
    line-height: 1.4;
`;
